import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Paper,
  Divider,
  TableContainer,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Image,
  NotificationsActive,
  PhotoCamera,
  YouTube,
  Close,
  VideoFile,
} from '@mui/icons-material';
import eventsService from '../../utils/eventsService';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';
import AIRewriteTextField from '../../components/AIRewriteTextField';
import moment from 'moment-jalaali';

moment.loadPersian({ usePersianDigits: false });

const formatJalaliInput = (isoString) => {
  if (!isoString) return '';
  const parsed = moment(isoString);
  if (!parsed.isValid()) return '';
  return parsed.format('jYYYY/jMM/jDD HH:mm');
};

const parseJalaliInput = (value) => {
  if (!value) return '';
  const normalized = value.replace(/[^0-9/:\s]/g, '').replace(/-/g, '/');
  const formats = [
    'jYYYY/jMM/jDD HH:mm',
    'jYYYY/jM/jD HH:mm',
    'jYYYY/jMM/jDD',
    'jYYYY/jM/jD',
    'YYYY/MM/DD HH:mm',
    'YYYY/MM/DD',
    'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DD'
  ];
  for (const format of formats) {
    const parsed = moment(normalized, format, true);
    if (parsed.isValid()) {
      return parsed.toISOString();
    }
  }
  return '';
};

const EventsManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    summary: '',
    posterImage: '',
    eventDate: '',
    endDate: '',
    eventDateJalali: '',
    endDateJalali: '',
    location: '',
    address: '',
    organizerName: '',
    organizerContact: '',
    capacity: '',
    registrationRequired: false,
    registrationDeadline: '',
    registrationDeadlineJalali: '',
    status: 'upcoming',
    isPublished: true,
    sendNotification: false,
    tags: '',
    gallery: [],
    videos: [],
    newVideoUrl: '',
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await eventsService.getEvents(1, 100);
      setEvents(response.data?.events || []);
    } catch (error) {
      console.error('Error loading events:', error);
      snackbar('خطا در بارگذاری مراسمات');
    }
  };

  const handleOpen = (event = null) => {
    if (event) {
      setCurrent(event);
      setForm({
        title: event.title || '',
        description: event.description || '',
        summary: event.summary || '',
        posterImage: event.posterImage || '',
        eventDate: event.eventDate || '',
        eventDateJalali: formatJalaliInput(event.eventDate || ''),
        endDate: event.endDate || '',
        endDateJalali: formatJalaliInput(event.endDate || ''),
        location: event.location || '',
        address: event.address || '',
        organizerName: event.organizerName || '',
        organizerContact: event.organizerContact || '',
        capacity: event.capacity || '',
        registrationRequired: event.registrationRequired || false,
        registrationDeadline: event.registrationDeadline || '',
        registrationDeadlineJalali: formatJalaliInput(event.registrationDeadline || ''),
        status: event.status || 'upcoming',
        isPublished: event.isPublished !== undefined ? event.isPublished : true,
        sendNotification: false,
        tags: event.tags?.join(', ') || '',
        gallery: event.gallery || [],
        videos: event.videos || [],
        newVideoUrl: '',
      });
    } else {
      setCurrent(null);
      setForm({
        title: '',
        description: '',
        summary: '',
        posterImage: '',
        eventDate: '',
        eventDateJalali: '',
        endDate: '',
        endDateJalali: '',
        location: '',
        address: '',
        organizerName: '',
        organizerContact: '',
        capacity: '',
        registrationRequired: false,
        registrationDeadline: '',
        registrationDeadlineJalali: '',
        status: 'upcoming',
        isPublished: true,
        sendNotification: false,
        tags: '',
        gallery: [],
        videos: [],
        newVideoUrl: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrent(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'eventDate' || name === 'endDate' || name === 'registrationDeadline') {
      const normalized = value.replace(/[^0-9/:\s]/g, '').replace(/-/g, '/');
      const iso = parseJalaliInput(normalized);
      setForm({
        ...form,
        [name]: iso,
        [`${name}Jalali`]: normalized,
      });
      return;
    }
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/events/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm({ ...form, posterImage: response.data.filename });
      successMessage('تصویر آپلود شد');
    } catch (error) {
      console.error('Error uploading image:', error);
      snackbar(error?.response?.data?.message || 'خطا در آپلود تصویر');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedImages = [];
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/events/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedImages.push(response.data.filename);
      }
      setForm({ ...form, gallery: [...form.gallery, ...uploadedImages] });
      successMessage('تصاویر به گالری اضافه شدند');
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      snackbar('خطا در آپلود تصاویر گالری');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (e.g., limit to 50MB for PWA)
    if (file.size > 50 * 1024 * 1024) {
      return snackbar('حجم ویدیو نباید بیشتر از ۵۰ مگابایت باشد');
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/events/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm({ ...form, videos: [...form.videos, response.data.filename] });
      successMessage('ویدیو آپلود و به لیست اضافه شد');
    } catch (error) {
      console.error('Error uploading video:', error);
      snackbar(error?.response?.data?.message || 'خطا در آپلود ویدیو');
    } finally {
      setUploading(false);
    }
  };

  const handleAddVideo = () => {
    if (!form.newVideoUrl || !form.newVideoUrl.trim()) return;
    setForm({
      ...form,
      videos: [...form.videos, form.newVideoUrl.trim()],
      newVideoUrl: '',
    });
  };

  const handleRemoveGalleryImage = (index) => {
    const newGallery = [...form.gallery];
    newGallery.splice(index, 1);
    setForm({ ...form, gallery: newGallery });
  };

  const handleRemoveVideo = (index) => {
    const newVideos = [...form.videos];
    newVideos.splice(index, 1);
    setForm({ ...form, videos: newVideos });
  };

  const handleSubmit = async () => {
    if (!form.title || !form.description || !form.eventDate) {
      return snackbar('عنوان، توضیحات و تاریخ مراسم الزامی است');
    }

    const {
      eventDateJalali,
      endDateJalali,
      registrationDeadlineJalali,
      newVideoUrl,
      ...eventDataBase
    } = form;
    const eventData = {
      ...eventDataBase,
      endDate: eventDataBase.endDate || undefined,
      registrationDeadline: eventDataBase.registrationDeadline || undefined,
      capacity: form.capacity ? parseInt(form.capacity) : undefined,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(t => t) : [],
    };

    try {
      if (current) {
        const { sendNotification, ...updatePayload } = eventData;
        await eventsService.updateEvent(current._id, updatePayload);
        successMessage('مراسم بروزرسانی شد');
      } else {
        await eventsService.createEvent(eventData);
        successMessage('مراسم جدید ایجاد شد');
      }
      handleClose();
      loadEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      snackbar('خطا در ذخیره مراسم');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این مراسم اطمینان دارید؟')) return;

    try {
      await eventsService.deleteEvent(id);
      successMessage('مراسم حذف شد');
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      snackbar('خطا در حذف مراسم');
    }
  };

  const handleResendNotification = async (id) => {
    if (!window.confirm('ارسال مجدد نوتیفیکیشن برای این مراسم انجام شود؟')) return;
    try {
      await api.post(`/events/${id}/send-notification`);
      successMessage('نوتیفیکیشن ارسال شد');
    } catch (error) {
      console.error('Error sending notification:', error);
      snackbar(error?.response?.data?.message || 'خطا در ارسال نوتیفیکیشن');
    }
  };

  const formatJalaliDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const getStatusLabel = (status) => {
    const labels = {
      upcoming: 'آینده',
      ongoing: 'در حال برگزاری',
      completed: 'برگزار شده',
      cancelled: 'لغو شده',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      upcoming: 'primary',
      ongoing: 'success',
      completed: 'default',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3, mt: isMobile ? 3 : 5 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between', 
        gap: 2,
        mb: 3, 
        mt: isMobile ? 6 : 5 
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">مدیریت مراسمات</Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={() => handleOpen()}
          fullWidth={isMobile}
          sx={{ borderRadius: '12px', py: isMobile ? 1.5 : 1 }}
        >
          مراسم جدید
        </Button>
      </Box>

      {isMobile ? (
        <Grid container spacing={2}>
          {events.map((event) => (
            <Grid item xs={12} key={event._id}>
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee', bgcolor: '#fafafa' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1, ml: 1 }}>{event.title}</Typography>
                    <Chip
                      label={getStatusLabel(event.status)}
                      color={getStatusColor(event.status)}
                      size="small"
                      sx={{ borderRadius: '8px' }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      📅 تاریخ: {formatJalaliDate(event.eventDate)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      📍 مکان: {event.location || '-'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      👁️ بازدید: {event.viewCount || 0}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <IconButton size="small" onClick={() => handleOpen(event)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <Edit fontSize="small" color="primary" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleResendNotification(event._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <NotificationsActive fontSize="small" color="info" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(event._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {events.length === 0 && (
             <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed' }}>
                  <Typography variant="body2" color="textSecondary">مراسمی یافت نشد</Typography>
                </Paper>
             </Grid>
          )}
        </Grid>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell fontWeight="bold">عنوان</TableCell>
                <TableCell>تاریخ</TableCell>
                {!isTablet && <TableCell>مکان</TableCell>}
                <TableCell align="center">وضعیت</TableCell>
                {!isTablet && <TableCell align="center">بازدید</TableCell>}
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event._id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{event.title}</TableCell>
                  <TableCell>{formatJalaliDate(event.eventDate)}</TableCell>
                  {!isTablet && <TableCell>{event.location || '-'}</TableCell>}
                  <TableCell align="center">
                    <Chip
                      label={getStatusLabel(event.status)}
                      color={getStatusColor(event.status)}
                      size="small"
                      sx={{ borderRadius: '8px' }}
                    />
                  </TableCell>
                  {!isTablet && <TableCell align="center">{event.viewCount || 0}</TableCell>}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleOpen(event)} title="ویرایش">
                        <Edit fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleResendNotification(event._id)} title="ارسال نوتیفیکیشن">
                        <NotificationsActive fontSize="small" color="info" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(event._id)} title="حذف">
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth 
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 4 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {current ? 'ویرایش مراسم' : 'ایجاد مراسم جدید'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label="عنوان مراسم"
                value={form.title}
                onChange={handleChange}
                fullWidth
                required
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <AIRewriteTextField
                value={form.summary}
                onChange={(value) => setForm({ ...form, summary: value })}
                label="خلاصه"
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <AIRewriteTextField
                value={form.description}
                onChange={(value) => setForm({ ...form, description: value })}
                label="توضیحات"
                rows={4}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="eventDate"
                label="تاریخ شروع"
                value={form.eventDateJalali}
                onChange={handleChange}
                fullWidth
                required
                placeholder="۱۴۰۳/۱۱/۰۳ ۰۹:۰۰"
                helperText="تاریخ و زمان شروع مراسم"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="endDate"
                label="تاریخ پایان"
                value={form.endDateJalali}
                onChange={handleChange}
                fullWidth
                placeholder="۱۴۰۳/۱۱/۰۳ ۱۷:۰۰"
                helperText="تاریخ و زمان پایان مراسم (اختیاری)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="location"
                label="مکان"
                value={form.location}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="address"
                label="آدرس"
                value={form.address}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="organizerName"
                label="نام برگزارکننده"
                value={form.organizerName}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="organizerContact"
                label="تماس برگزارکننده"
                value={form.organizerContact}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="capacity"
                label="ظرفیت"
                type="number"
                value={form.capacity}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="registrationDeadline"
                label="مهلت ثبت‌نام"
                value={form.registrationDeadlineJalali}
                onChange={handleChange}
                fullWidth
                placeholder="۱۴۰۳/۱۱/۰۱ ۲۳:۵۹"
                helperText="آخرین مهلت ثبت‌نام (اختیاری)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>وضعیت</InputLabel>
                <Select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  label="وضعیت"
                >
                  <MenuItem value="upcoming">آینده</MenuItem>
                  <MenuItem value="ongoing">در حال برگزاری</MenuItem>
                  <MenuItem value="completed">برگزار شده</MenuItem>
                  <MenuItem value="cancelled">لغو شده</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="tags"
                label="برچسب‌ها (با ویرگول جدا کنید)"
                value={form.tags}
                onChange={handleChange}
                fullWidth
                placeholder="مثال: کنفرانس، آموزشی، علمی"
              />
            </Grid>

            {form.status === 'completed' && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                    گالری تصاویر و ویدیوها
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" sx={{ flexGrow: 1 }}>تصاویر گالری</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        component="label"
                        startIcon={<PhotoCamera />}
                        disabled={uploading}
                      >
                        افزودن تصویر
                        <input type="file" hidden accept="image/*" multiple onChange={handleGalleryUpload} />
                      </Button>
                    </Box>
                    <Grid container spacing={1}>
                      {form.gallery.map((img, idx) => (
                        <Grid item key={idx} xs={4} sm={2}>
                          <Box sx={{ position: 'relative' }}>
                            <img
                              src={`${process.env.REACT_APP_FILE_BASE_URL}/uploads/documents/${img}`}
                              alt=""
                              style={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 8 }}
                            />
                            <IconButton
                              size="small"
                              sx={{ 
                                position: 'absolute', top: -8, right: -8, 
                                bgcolor: 'white', border: '1px solid #eee',
                                '&:hover': { bgcolor: '#f5f5f5' }
                              }}
                              onClick={() => handleRemoveGalleryImage(idx)}
                            >
                              <Close sx={{ fontSize: 12 }} color="error" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>ویدیوها (آپلود مستقیم یا لینک)</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="لینک ویدیو یا نام فایل"
                        name="newVideoUrl"
                        value={form.newVideoUrl}
                        onChange={handleChange}
                        placeholder="لینک ویدئو را وارد کنید"
                      />
                      <Button variant="contained" size="small" onClick={handleAddVideo} startIcon={<Add />}>
                        تایید
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        component="label"
                        startIcon={<YouTube />}
                        disabled={uploading}
                        sx={{ borderStyle: 'dashed', borderRadius: '8px' }}
                      >
                        {uploading ? 'در حال آپلود...' : 'انتخاب و آپلود مستقیم ویدئو'}
                        <input type="file" hidden accept="video/*" onChange={handleVideoUpload} />
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {form.videos.map((vid, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', bgcolor: 'white', borderRadius: 2 }}>
                          <VideoFile sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                          <Typography variant="caption" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {vid}
                          </Typography>
                          <IconButton size="small" onClick={() => handleRemoveVideo(idx)}>
                            <Delete sx={{ fontSize: 16 }} color="error" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={form.sendNotification} onChange={handleChange} name="sendNotification" disabled={!!current} />}
                label="ارسال نوتیفیکیشن"
              />
            </Grid>

            <Grid item xs={12}>
              <Button 
                variant="outlined" 
                component="label" 
                fullWidth 
                disabled={uploading}
                sx={{ borderRadius: '12px', borderStyle: 'dashed', py: 1.5 }}
              >
                <Image sx={{ mr: 1 }} />
                {uploading ? 'در حال آپلود...' : 'آپلود پوستر مراسم'}
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
              </Button>
              {form.posterImage && (
                <Box sx={{ mt: 2, position: 'relative' }}>
                  <img 
                    src={`${process.env.REACT_APP_FILE_BASE_URL}/uploads/documents/${form.posterImage}`} 
                    alt="Poster" 
                    style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }} 
                  />
                  <IconButton 
                    size="small" 
                    onClick={() => setForm({ ...form, posterImage: '' })}
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.8)' }}
                  >
                    <Close fontSize="small" color="error" />
                  </IconButton>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} sx={{ borderRadius: '8px' }}>لغو</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: '8px', px: 4 }}>
            ذخیره تغییرات
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsManagement;
